import { DEFAULT_MANAGER_EMAIL_FIELD } from "@p0security/shared/types/directory";
import { directoryInfo } from "@p0security/shared/types/policy/constants";
import {
  Directory,
  RequestorProfile,
} from "@p0security/shared/types/policy/types";
import { Button, Select, Typography } from "@p0security/ui";
import { FormInput } from "components/common/forms/FormInput";
import {
  StyledFormItem,
  standardFormItemCols,
} from "components/common/forms/styles";
import { useCallback } from "react";
import { useInstalledDirectories } from "store/resources/DirectoriesProvider";

type Props = {
  value: RequestorProfile;
  onChange: (value: RequestorProfile) => void;
};

const PROFILE_ATTRIBUTE_LABEL = "Profile attribute that contains manager email";

export const RequestorProfileInput: React.FC<Props> = ({ value, onChange }) => {
  const { availableDirectories } = useInstalledDirectories();

  const switchDirectory = useCallback(
    (directory: Directory) => onChange({ ...value, directory }),
    [onChange, value]
  );

  const setProfileProperty = useCallback(
    (property?: string) => onChange({ ...value, profileProperty: property }),
    [onChange, value]
  );
  const setDefaultProfileProperty = useCallback(
    () => setProfileProperty(DEFAULT_MANAGER_EMAIL_FIELD),
    [setProfileProperty]
  );
  const clearProfileProperty = useCallback(
    () => setProfileProperty(),
    [setProfileProperty]
  );

  return availableDirectories.state === "loading" ? (
    <div>Loading directories...</div>
  ) : (
    <>
      <StyledFormItem label="Directory" {...standardFormItemCols}>
        <Select
          options={availableDirectories.directories.map((d) => ({
            label: directoryInfo[d].label,
            value: d,
          }))}
          value={value.directory}
          onChange={switchDirectory}
        />
      </StyledFormItem>
      {value.directory === "okta" ? (
        value.profileProperty !== undefined ? (
          <FormInput
            label={PROFILE_ATTRIBUTE_LABEL}
            value={value.profileProperty}
            onChange={setProfileProperty}
            extra={
              <Button
                type="text"
                size="small"
                disabled={value.profileProperty === DEFAULT_MANAGER_EMAIL_FIELD}
                onClick={clearProfileProperty}
              >
                Reset to default profile attribute
              </Button>
            }
          />
        ) : (
          <StyledFormItem
            label={PROFILE_ATTRIBUTE_LABEL}
            {...standardFormItemCols}
          >
            <Typography.Text code>
              {DEFAULT_MANAGER_EMAIL_FIELD}
            </Typography.Text>
            <Button
              type="link"
              size="small"
              onClick={setDefaultProfileProperty}
            >
              Override profile property
            </Button>
          </StyledFormItem>
        )
      ) : null}
    </>
  );
};
